import React from 'react';
import { Link } from 'react-router-dom';
import { Home as HomeIcon, Building2, Check, ArrowRight, DollarSign, Clock, Shield, Users, Wrench, Camera, MessageCircle, BarChart3, FileText, Zap, AlertTriangle, Scale } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import SavingsCalculator from '../components/SavingsCalculator';
import SEO, { SEOConfig } from '../components/SEO';

export default function ForLandlords() {
  const benefits = [
    {
      icon: DollarSign,
      title: "6% Full Management Fee (UK Wide)",
      description: "Lower cost than traditional agents, with full management features built-in. No hidden fees.",
      highlight: true
    },
    {
      icon: Clock,
      title: "Faster Letting, Less Admin",
      description: "Tenant applications, screening, and document collection are handled in-app with clear status tracking."
    },
    {
      icon: Shield,
      title: "Digital Tenant Vetting & Checks",
      description: "ID verification, affordability checks, and referencing workflows built into the platform."
    },
    {
      icon: Camera,
      title: "Modern Remote Viewings & Media",
      description: "Walkthrough video and 3D model support improves tenant confidence and reduces wasted viewings."
    },
    {
      icon: BarChart3,
      title: "Smarter Inspections (Less Intrusion)",
      description: "Tenant-led inspection windows with evidence capture and automated comparison against baseline condition."
    },
    {
      icon: Wrench,
      title: "Repairs Without the Back-and-Forth",
      description: "Tenants report issues in-app. Repairs route automatically: auto-approve up to your limit, escalate above."
    },
    {
      icon: Users,
      title: "Contractor Network + First-to-Accept",
      description: "Jobs go instantly to vetted local contractors. The first suitable contractor to accept gets it."
    },
    {
      icon: MessageCircle,
      title: "Disputes Handled in One Place",
      description: "All communication and evidence is retained. Dispute workflow first, escalation only when needed."
    },
    {
      icon: FileText,
      title: "Clear Statements & Audit Trail",
      description: "Every action is logged: approvals, repairs, messages, inspections, decisions. Full transparency."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-slate-50">
      <SEO {...SEOConfig.forLandlords} />
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="p-2 bg-primary rounded-lg">
              <HomeIcon className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-display font-extrabold text-primary">LetWise</h1>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/landlords" className="text-green-600 font-semibold">
              For Landlords
            </Link>
            <Link to="/tenants" className="text-slate-700 hover:text-primary transition-colors font-semibold">
              For Tenants
            </Link>
            <Link to="/properties" className="text-slate-600 hover:text-primary transition-colors font-medium">
              Browse Properties
            </Link>
            <Link to="/login">
              <Button>Sign In</Button>
            </Link>
          </nav>
          <div className="flex md:hidden items-center gap-3">
            <Link to="/tenants" className="text-sm text-slate-700 font-medium">Tenants</Link>
            <Link to="/login"><Button size="sm">Sign In</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-24">
        {/* RRA 2025 Alert Banner */}
        <Link 
          to="/renters-rights-act"
          className="block mb-8 p-4 bg-gradient-to-r from-emerald-900 to-teal-800 rounded-xl text-white hover:from-emerald-800 hover:to-teal-700 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-amber-400 uppercase tracking-wide">New Legislation</span>
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                  </span>
                </div>
                <p className="font-bold">Renters' Rights Act 2025 — Are You Ready?</p>
                <p className="text-emerald-200 text-sm">Section 21 ends May 2026. Learn how LetWise makes compliance easy.</p>
              </div>
            </div>
            <ArrowRight className="w-6 h-6 text-emerald-300 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>

        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-6">
            <Building2 className="w-4 h-4" />
            For Landlords
          </div>
          <div className="inline-block bg-gradient-to-r from-green-500 to-emerald-500 text-white text-lg font-bold px-4 py-2 rounded-lg mb-4">
            Full Management from 6% — UK Wide
          </div>
          <h1 className="text-4xl md:text-5xl font-display font-extrabold leading-tight mb-6">
            Full property management at 6%
            <span className="text-green-600 block">with less hassle and more control.</span>
          </h1>
          <p className="text-xl text-slate-600 mb-8 leading-relaxed">
            LetWise automates the admin that costs you money: applications, compliance, inspections, repairs, and dispute handling — all in one platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/register?role=landlord">
              <Button size="lg" className="w-full sm:w-auto bg-green-600 hover:bg-green-700">
                Start Landlord Onboarding
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="outline" 
              className="w-full sm:w-auto"
              onClick={() => window.location.href = 'mailto:demo@letwise.com?subject=Demo Request'}
            >
              Book a Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="bg-white py-6 border-y">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="flex items-center gap-2 text-slate-700">
              <Check className="w-5 h-5 text-green-600" />
              <span className="font-medium">Transparent Workflows</span>
            </div>
            <div className="flex items-center gap-2 text-slate-700">
              <Check className="w-5 h-5 text-green-600" />
              <span className="font-medium">Compliance-Focused</span>
            </div>
            <div className="flex items-center gap-2 text-slate-700">
              <Check className="w-5 h-5 text-green-600" />
              <span className="font-medium">Designed for Scale</span>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-display font-bold text-center mb-4">Landlord Benefits</h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Everything you need to manage your portfolio efficiently
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className={`p-6 hover:shadow-lg transition-shadow ${benefit.highlight ? 'border-2 border-green-500 bg-green-50' : ''}`}>
                  <div className={`p-3 ${benefit.highlight ? 'bg-green-200' : 'bg-green-100'} rounded-lg w-fit mb-4`}>
                    <Icon className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">{benefit.title}</h3>
                  <p className="text-slate-600 text-sm">{benefit.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Compliance as a Service Section */}
      <section className="py-16 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                Compliance as a Service
              </div>
              <h2 className="text-4xl font-display font-bold mb-6">
                Never worry about compliance again
              </h2>
              <p className="text-xl opacity-90 mb-8">
                We handle all your property certificates. Order through the platform, we assign vetted contractors, upload certificates automatically. One less headache for each property.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 flex-shrink-0" />
                  <span><strong>Per-property tracking</strong> - Each property has unique requirements based on features and location</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 flex-shrink-0" />
                  <span><strong>Automatic reminders</strong> - Get notified 30 days before certificates expire</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 flex-shrink-0" />
                  <span><strong>Block tenancy signing</strong> - System prevents signing if compliance incomplete</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 flex-shrink-0" />
                  <span><strong>Jurisdiction-aware</strong> - Scotland, England, Wales have different rules</span>
                </li>
              </ul>
            </div>
            <Card className="p-8 bg-white text-slate-900">
              <h3 className="font-bold text-xl mb-6 text-emerald-700">Service Pricing</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <span className="font-semibold">Gas Safety (CP12)</span>
                    <span className="block text-sm text-slate-500">Annual inspection</span>
                  </div>
                  <span className="font-bold text-emerald-600 text-xl">£75</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <span className="font-semibold">EICR</span>
                    <span className="block text-sm text-slate-500">5-year electrical check</span>
                  </div>
                  <span className="font-bold text-emerald-600 text-xl">£150</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <span className="font-semibold">EPC</span>
                    <span className="block text-sm text-slate-500">10-year energy rating</span>
                  </div>
                  <span className="font-bold text-emerald-600 text-xl">£85</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <span className="font-semibold">Smoke & CO Alarms</span>
                    <span className="block text-sm text-slate-500">Install & test</span>
                  </div>
                  <span className="font-bold text-emerald-600 text-xl">£50</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <span className="font-semibold">Legionella Risk</span>
                    <span className="block text-sm text-slate-500">Water safety assessment</span>
                  </div>
                  <span className="font-bold text-emerald-600 text-xl">£95</span>
                </div>
              </div>
              <p className="text-sm text-slate-500 mt-4 text-center">
                All services include vetted contractors and automatic certificate upload
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Repair Rules Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-display font-bold mb-6">Smart Repair Routing</h2>
              <p className="text-lg text-slate-600 mb-6">
                Set your own rules for automatic repair approval. No more back-and-forth for small fixes.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <span><strong>Auto-approve</strong> repairs up to your chosen limit (e.g., £150)</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <span><strong>Escalate</strong> above limit for your approval</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <span><strong>Emergency handling</strong> with separate limits</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <span><strong>First-to-accept</strong> contractor assignment</span>
                </li>
              </ul>
            </div>
            <Card className="p-8 bg-slate-50">
              <h3 className="font-bold mb-6">Your Repair Rules</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-white rounded-lg">
                  <span>Auto-approve limit</span>
                  <span className="font-bold text-green-600">£150</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-white rounded-lg">
                  <span>Emergency limit</span>
                  <span className="font-bold text-green-600">£500</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-white rounded-lg">
                  <span>Contractor network</span>
                  <span className="font-bold text-green-600">Platform vetted</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* AI Smart Inspection Engine Section */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-sm font-medium mb-6">
                <Camera className="w-4 h-4" />
                AI Smart Inspection Engine
              </div>
              <h2 className="text-3xl font-display font-bold mb-6">
                Protect Your Property with Intelligent Inspections
              </h2>
              <p className="text-lg opacity-90 mb-6">
                Our AI-powered inspection system captures baseline property condition and automatically detects 
                changes over time. Get clear evidence for deposit discussions without arguments.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-purple-300" />
                  <span>Before/after comparison reports</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-purple-300" />
                  <span>Automatic damage detection</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-purple-300" />
                  <span>Fair wear vs damage classification</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-purple-300" />
                  <span>Tenant-led inspections reduce intrusion</span>
                </li>
              </ul>
            </div>
            <Card className="p-8 bg-white text-slate-900">
              <h3 className="font-bold text-xl mb-6 text-purple-700">Inspection Pricing</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <span className="font-semibold">Baseline Property Scan</span>
                    <span className="block text-sm text-slate-500">Full property documentation</span>
                  </div>
                  <span className="font-bold text-purple-600 text-xl">£95</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <span className="font-semibold">Tenant Inspection Window</span>
                    <span className="block text-sm text-slate-500">Self-service with AI comparison</span>
                  </div>
                  <span className="font-bold text-purple-600 text-xl">£45</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <span className="font-semibold">Check-Out Comparison</span>
                    <span className="block text-sm text-slate-500">Full damage assessment</span>
                  </div>
                  <span className="font-bold text-purple-600 text-xl">£75</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Savings Calculator Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-display font-bold mb-4">How Much Could You Save?</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Use our calculator to compare your current costs with LetWise management tiers
            </p>
          </div>
          <SavingsCalculator />
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <Card className="p-12 text-center bg-gradient-to-r from-green-600 to-emerald-600 text-white">
            <h2 className="text-3xl font-display font-bold mb-4">Ready to simplify your lettings?</h2>
            <p className="text-xl mb-8 opacity-90">Join landlords across the UK saving time and money with LetWise.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register?role=landlord">
                <Button size="lg" className="bg-white text-green-600 hover:bg-slate-100">
                  Start Landlord Onboarding
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10"
                onClick={() => window.location.href = 'mailto:demo@letwise.com?subject=Demo Request'}
              >
                Book a Demo
              </Button>
            </div>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-slate-400">&copy; 2025 LetWise. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
